import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import {
  type GroupRow,
  SortableGroupListItem,
  SortableListItem,
  StaticListItem,
} from '@/components/shared/sortable-list-item';

type BaseItem = {
  id: string;
  className?: string;
};

export type SortableSettingsListItem =
  | (BaseItem & {
      type: 'static';
      label: React.ReactNode;
      action: React.ReactNode;
      body?: React.ReactNode;
    })
  | (BaseItem & {
      type: 'sortable';
      label: React.ReactNode;
      action: React.ReactNode;
      body?: React.ReactNode;
    })
  | (BaseItem & {
      type: 'group';
      rows: GroupRow[];
    });

function isDraggableItem(
  item: SortableSettingsListItem,
): item is Extract<SortableSettingsListItem, { type: 'sortable' | 'group' }> {
  return item.type === 'sortable' || item.type === 'group';
}

export function reorderSortableSettingsItems(
  items: SortableSettingsListItem[],
  activeId: string,
  overId: string,
): SortableSettingsListItem[] {
  const draggableItems = items.filter(isDraggableItem);
  const oldIndex = draggableItems.findIndex((item) => item.id === activeId);
  const newIndex = draggableItems.findIndex((item) => item.id === overId);

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return items;
  }

  const reorderedDraggableItems = arrayMove(draggableItems, oldIndex, newIndex);
  let draggableIndex = 0;

  return items.map((item) => {
    if (!isDraggableItem(item)) {
      return item;
    }

    const nextItem = reorderedDraggableItems[draggableIndex];
    draggableIndex += 1;
    return nextItem;
  });
}

export function SortableSettingsList({
  items,
  onReorder,
}: {
  items: SortableSettingsListItem[];
  onReorder: (nextItems: SortableSettingsListItem[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const draggableIds = items.filter(isDraggableItem).map((item) => item.id);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const nextItems = reorderSortableSettingsItems(items, String(active.id), String(over.id));
    if (nextItems !== items) {
      onReorder(nextItems);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={draggableIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {items.map((item) => {
            if (item.type === 'static') {
              return (
                <StaticListItem
                  key={item.id}
                  label={item.label}
                  action={item.action}
                  body={item.body}
                  className={item.className}
                />
              );
            }

            if (item.type === 'group') {
              return (
                <SortableGroupListItem
                  key={item.id}
                  id={item.id}
                  rows={item.rows}
                  className={item.className}
                />
              );
            }

            return (
              <SortableListItem
                key={item.id}
                id={item.id}
                label={item.label}
                action={item.action}
                body={item.body}
                className={item.className}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}
